import { get, isEmpty, isNil, isNumber } from 'lodash'
import * as Types from '../../types'
import surveyQuestionBank from '../../../communityConfigs/survey-questions.json'
import { SURVEY_ATTEMPT_STATES } from 'config'
import {
  filterCoursesByQuestionCategory,
  hasZeroMinutesInCourses,
  hasZeroStudentsEnrolled,
  isMissingInstructionalDepth,
  isMissingMinutesInCourses,
  isMissingNumberOfStudentsEnrolled,
} from 'utils'

export const SECTION_KEYS = {
  STAFFING_INSTRUCTION: 'staffing_and_instruction',
  COMMUNITY_PARTNERSHIPS: 'community_and_partnerships',
  BUDGET_PLANNING: 'budget_and_planning',
  SUBMIT: 'submit',
}

export const SECTION_META = {
  [SECTION_KEYS.STAFFING_INSTRUCTION]: {
    label: 'Staffing & Instruction',
  },
  [SECTION_KEYS.COMMUNITY_PARTNERSHIPS]: {
    label: 'Community & Partnerships',
  },
  [SECTION_KEYS.BUDGET_PLANNING]: {
    label: 'Budget & Planning',
  },
  [SECTION_KEYS.SUBMIT]: {
    label: 'Submit',
  },
}

const { REQUIRED } = Types.SURVEY_QUESTIONS_NECESSITIES

const {
  INSTRUCTIONAL_DEPTH_COURSE_FORM,
  WEEKLY_MINUTES_COURSE_FORM,
  ARTS_INSTRUCTOR_FORM,
  SINGLE_STANDARD_FORM,
  FUNDING_FORM,
  CUSTOM_QUESTION_FORM,
  SPACE_TYPES_FORM,
  MULTIPLE_STANDARDS_FORM,
} = Types.QUESTION_DISPLAY_COMPONENTS

const {
  APPROACHES,
  INTEGRATIONS,
  GOVERNANCE,
  DISTRICT_SPENDING,
  COMMUNITY_ENGAGEMENTS,
} = Types.SURVEY_QUESTIONS

export function getSections() {
  return [
    SECTION_KEYS.STAFFING_INSTRUCTION,
    SECTION_KEYS.COMMUNITY_PARTNERSHIPS,
    SECTION_KEYS.BUDGET_PLANNING,
    SECTION_KEYS.SUBMIT,
  ]
}

export function computePercent(completedCount, totalCount) {
  if (!totalCount) return 0
  if (completedCount <= 0) return 0
  return Math.round((completedCount / totalCount) * 100)
}

function validateCourseInstructionalDepth(school) {
  const filteredCourses = filterCoursesByQuestionCategory(
    school.courses || [],
    Types.HIGH_SCHOOL_TYPE
  )

  return (
    isMissingInstructionalDepth(filteredCourses) ||
    isMissingNumberOfStudentsEnrolled(filteredCourses) ||
    hasZeroStudentsEnrolled(filteredCourses)
  )
}

function validateCourseWeeklyMinutes(school) {
  const filteredCourses = filterCoursesByQuestionCategory(
    school.courses || [],
    [Types.ELEMENTARY_SCHOOL_TYPE, Types.MIDDLE_SCHOOL_TYPE]
  )

  return (
    isMissingMinutesInCourses(filteredCourses) ||
    isMissingNumberOfStudentsEnrolled(filteredCourses) ||
    hasZeroMinutesInCourses(filteredCourses) ||
    hasZeroStudentsEnrolled(filteredCourses)
  )
}

function validateCoursePresence(school, schoolCategories) {
  return !isEmpty(
    filterCoursesByQuestionCategory(school.courses || [], schoolCategories)
  )
}

function validateInstructorsSchedule(school) {
  return (school.instructors || []).every((instructor) => instructor.schedule)
}

function validateInstructorsPrimaryFocus(school) {
  return (school.instructors || []).every((instructor) => {
    if (instructor.primaryFocus) return true

    const focuses = instructor.focuses
    if (!Array.isArray(focuses)) return false

    return focuses.some(
      (focus) =>
        focus && focus.primary && (focus.focusableId || focus.focusable_id)
    )
  })
}

function validatePercentAccess(school, participation) {
  if (school.category === Types.HIGH_SCHOOL_TYPE) return true
  return isNumber(get(participation, 'courseEnrollment'))
}

function validateCustomAnswer(school, questionId) {
  return (school.surveyAnswers || []).some(
    (answer) => answer.surveyQuestionId === questionId
  )
}

function getRequiredQuestionsForPage(surveyQuestions, surveyPage) {
  return (surveyQuestions || []).filter(
    (question) =>
      question.surveyPage === surveyPage && question.necessity === REQUIRED
  )
}

function getCommunityQuestionOption(communitySurveyQuestions, questionName) {
  const optionName = get(communitySurveyQuestions, questionName)
  if (!optionName) return null

  return get(surveyQuestionBank, `${questionName}.${optionName}`)
}

function hasRequiredConfiguredResponse(
  school,
  communitySurveyQuestions,
  questionName
) {
  const option = getCommunityQuestionOption(
    communitySurveyQuestions,
    questionName
  )
  if (!option || !option.error) return true

  return !isEmpty(get(school, option.responseType))
}

function hasRequiredQuestionResponse(question, school) {
  switch (question.displayComponent) {
    case INSTRUCTIONAL_DEPTH_COURSE_FORM:
      if (!validateCoursePresence(school, Types.HIGH_SCHOOL_TYPE)) return true
      return !validateCourseInstructionalDepth(school)
    case WEEKLY_MINUTES_COURSE_FORM:
      if (
        !validateCoursePresence(school, [
          Types.ELEMENTARY_SCHOOL_TYPE,
          Types.MIDDLE_SCHOOL_TYPE,
        ])
      ) {
        return true
      }
      return !validateCourseWeeklyMinutes(school)
    case ARTS_INSTRUCTOR_FORM:
      if (isEmpty(school.instructors)) return true
      return (
        validateInstructorsSchedule(school) &&
        validateInstructorsPrimaryFocus(school)
      )
    case SINGLE_STANDARD_FORM:
    case MULTIPLE_STANDARDS_FORM:
      return !isEmpty(school.standards)
    case SPACE_TYPES_FORM:
      return !isEmpty(school.spaces)
    case FUNDING_FORM:
      return !isEmpty(school.fundings)
    case CUSTOM_QUESTION_FORM:
      return validateCustomAnswer(school, question.id)
    default:
      return true
  }
}

function isStaffingInstructionComplete(data) {
  const { school, participation, surveyQuestions, communitySurveyQuestions } =
    data
  const requiredQuestions = getRequiredQuestionsForPage(
    surveyQuestions,
    Types.SURVEY_PAGES.STAFFING_AND_INSTRUCTION
  )

  return (
    validatePercentAccess(school, participation) &&
    hasRequiredConfiguredResponse(
      school,
      communitySurveyQuestions,
      APPROACHES.NAME
    ) &&
    hasRequiredConfiguredResponse(
      school,
      communitySurveyQuestions,
      INTEGRATIONS.NAME
    ) &&
    !isNil(get(school, 'surveyDetail.professionalDevelopment')) &&
    !isEmpty(school.spaces) &&
    requiredQuestions.every((question) =>
      hasRequiredQuestionResponse(question, school)
    )
  )
}

function isCommunityPartnershipsComplete(data) {
  const { school, surveyQuestions, communitySurveyQuestions } = data
  const requiredQuestions = getRequiredQuestionsForPage(
    surveyQuestions,
    Types.SURVEY_PAGES.COMMUNITY_AND_PARTNERSHIPS
  )

  return (
    (!get(communitySurveyQuestions, COMMUNITY_ENGAGEMENTS.NAME) ||
      !isEmpty(school.engagements)) &&
    requiredQuestions.every((question) =>
      hasRequiredQuestionResponse(question, school)
    )
  )
}

function isBudgetPlanningComplete(data) {
  const { school, surveyQuestions, communitySurveyQuestions } = data
  const requiredQuestions = getRequiredQuestionsForPage(
    surveyQuestions,
    Types.SURVEY_PAGES.BUDGET_AND_PLANNING
  )

  return (
    hasRequiredConfiguredResponse(
      school,
      communitySurveyQuestions,
      GOVERNANCE.NAME
    ) &&
    hasRequiredConfiguredResponse(
      school,
      communitySurveyQuestions,
      DISTRICT_SPENDING.NAME
    ) &&
    requiredQuestions.every((question) =>
      hasRequiredQuestionResponse(question, school)
    )
  )
}

export function isSectionComplete(sectionKey, data) {
  const { school } = data
  if (!school) return false

  switch (sectionKey) {
    case SECTION_KEYS.STAFFING_INSTRUCTION:
      return isStaffingInstructionComplete(data)
    case SECTION_KEYS.COMMUNITY_PARTNERSHIPS:
      return isCommunityPartnershipsComplete(data)
    case SECTION_KEYS.BUDGET_PLANNING:
      return isBudgetPlanningComplete(data)
    case SECTION_KEYS.SUBMIT:
      return get(data, 'surveyAttempt.state') === SURVEY_ATTEMPT_STATES.COMPLETE
    default:
      return false
  }
}

export function deriveCompletedSections(data) {
  return getSections().filter((sectionKey) =>
    isSectionComplete(sectionKey, data)
  )
}

export function buildBarModel(data) {
  const sections = getSections()
  const completedSections = deriveCompletedSections(data)
  const completedSet = new Set(completedSections)
  const orderedCompletedSections = sections.filter((sectionKey) =>
    completedSet.has(sectionKey)
  )
  const orderedIncompleteSections = sections.filter(
    (sectionKey) => !completedSet.has(sectionKey)
  )

  return {
    sections: [...orderedCompletedSections, ...orderedIncompleteSections].map(
      (key) => ({ key, ...SECTION_META[key] })
    ),
    completedSections: orderedCompletedSections,
    percent: computePercent(completedSections.length, sections.length),
    totalCount: sections.length,
  }
}
